import type { SvgIconComponent } from '@mui/icons-material';
import { Chip, Tooltip as MuiTooltip } from '@mui/material';

export type EntityChipsSize = 'small' | 'medium';

const CHIP_SX_BY_SIZE: Record<EntityChipsSize, { fontSize: string; height: number }> = {
  small: { fontSize: '0.7rem', height: 22 },
  medium: { fontSize: '0.8125rem', height: 28 },
};

/** Icon fontSize per size; exported for use in section headers (e.g. TopicEntities). */
export const ENTITY_CHIP_ICON_FONT_SIZE_BY_SIZE: Record<EntityChipsSize, number> = {
  small: 14,
  medium: 18,
};

/**
 * Returns icon sx for the given size. Use in section headers (e.g. TopicEntities) to match chip icon size.
 */
export function getEntityChipIconSx(size: EntityChipsSize): { fontSize: number } {
  return { fontSize: ENTITY_CHIP_ICON_FONT_SIZE_BY_SIZE[size] };
}

/**
 * Renders a list of entity names as chips, with visible items and an optional "+N" chip with tooltip.
 * When showIconOnEachChip is true (short mode), every chip shows the icon; otherwise only the "+N" chip does (long mode).
 * @param maxPerType - Maximum number of entity chips to show per type before collapsing the rest into a "+N" chip. Defaults to 2.
 */
export function EntityChips({
  names,
  keyPrefix,
  Icon,
  showIconOnEachChip,
  size = 'small',
  maxPerType = 2,
}: {
  names: string[];
  keyPrefix: string;
  Icon: SvgIconComponent;
  showIconOnEachChip: boolean;
  size?: EntityChipsSize;
  maxPerType?: number;
}) {
  const visible = names.slice(0, maxPerType);
  const remaining = names.slice(maxPerType);
  const chipSx = CHIP_SX_BY_SIZE[size];
  const iconSx = getEntityChipIconSx(size);
  const iconNode = <Icon sx={iconSx} color="secondary" />;

  return (
    <>
      {visible.map((name, i) => (
        <Chip
          key={`${keyPrefix}-${name}-${i}`}
          icon={showIconOnEachChip ? iconNode : undefined}
          label={name}
          size={size}
          variant="outlined"
          sx={chipSx}
        />
      ))}
      {remaining.length > 0 && (
        <MuiTooltip title={remaining.join(', ')} placement="top">
          <Chip
            icon={iconNode}
            label={`+${remaining.length}`}
            size={size}
            variant="outlined"
            sx={chipSx}
          />
        </MuiTooltip>
      )}
    </>
  );
}
